import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Truck } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { fmtPrice, prodName, prodTagline } from "../data/products";

export const QuickView = () => {
  const { L, lang, products, quickView, setQuickView, addToCart, setCartOpen } = useStore();
  const [qty, setQty] = useState(1);
  const p = quickView;

  useEffect(() => {
    setQty(1);
  }, [p && p.slug]);

  const close = () => setQuickView(null);

  const related = p ? products.filter((r) => r.collection === p.collection && r.slug !== p.slug).slice(0, 3) : [];

  const add = () => {
    addToCart(p.slug, qty);
    setQuickView(null);
    setCartOpen(true);
  };

  return (
    <AnimatePresence>
      {p && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="quick-view-modal"
        >
          <div className="absolute inset-0 bg-obsidian/85 backdrop-blur-sm" onClick={close} />
          <motion.div
            className="relative bg-onyx border border-gold/25 rounded-2xl overflow-hidden max-w-3xl w-full grid md:grid-cols-2 max-h-[90vh]"
            initial={{ y: 40, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <button data-testid="quick-view-close-button" onClick={close} aria-label="Close" className="absolute top-3 right-3 z-10 p-2 text-ivory/70 hover:text-gold transition-colors">
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>

            <div className="relative bg-obsidian">
              <div className="h-56 md:h-full md:min-h-[440px] overflow-hidden">
                <img src={p.image} alt={prodName(p, lang)} className="w-full h-full object-cover" data-testid="quick-view-image" />
              </div>
              <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 -60px 80px -40px rgba(12,10,9,0.9)" }} />
            </div>

            <div className="p-7 md:p-9 flex flex-col gap-4 overflow-y-auto">
              <div className="flex items-center gap-2">
                <p className="eyebrow" data-testid="quick-view-size">{L.product.size}</p>
                {p.unisex && <span className="text-[0.55rem] tracking-[0.2em] uppercase border border-gold/40 text-gold/90 rounded-full px-2.5 py-0.5" data-testid="quick-view-unisex">{L.product.unisex}</span>}
              </div>
              <h3 className="font-display text-3xl md:text-4xl text-ivory leading-tight" data-testid="quick-view-name">
                {prodName(p, lang)}
              </h3>
              <div className="flex items-center justify-between">
                <p className="text-gold font-display text-2xl" data-testid="quick-view-price">{fmtPrice(p.price, lang)}</p>
                <p className="flex items-center gap-1.5 text-[0.68rem] tracking-wide text-emerald-300/80" data-testid="quick-view-stock">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400/90" />
                  {L.shop.inStock}
                </p>
              </div>
              <p className="text-sand text-sm leading-relaxed" data-testid="quick-view-tagline">{prodTagline(p, lang)}</p>

              {p.notes && (
                <div className="space-y-2.5 border-t border-gold/15 pt-4" data-testid="quick-view-notes">
                  {[["top", L.product.notesTop], ["heart", L.product.notesHeart], ["base", L.product.notesBase]].map(([k, label]) => (
                    <div key={k}>
                      <p className="text-[0.58rem] tracking-[0.25em] uppercase text-gold/80">{label}</p>
                      <p className="text-ivory/80 text-[0.83rem] mt-0.5">{(p.notes[k][lang] || p.notes[k].en).join(" · ")}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-4 mt-auto pt-2">
                <div className="flex items-center border border-gold/30 rounded-full" data-testid="quick-view-qty">
                  <button data-testid="quick-view-qty-decrease" onClick={() => setQty(Math.max(1, qty - 1))} className="p-2.5 text-ivory/70 hover:text-gold" aria-label="minus">
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-8 text-center text-ivory" data-testid="quick-view-qty-value">{qty}</span>
                  <button data-testid="quick-view-qty-increase" onClick={() => setQty(Math.min(99, qty + 1))} className="p-2.5 text-ivory/70 hover:text-gold" aria-label="plus">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <button data-testid="quick-view-add-button" onClick={add} className="btn-gold flex-1 rounded-full py-3 text-[0.7rem] tracking-[0.25em] uppercase font-semibold">
                  {L.product.add}
                </button>
              </div>

              <div className="flex items-start gap-2.5 p-3.5 rounded-xl border border-gold/25 bg-gold/5" data-testid="quick-view-free-shipping-offer">
                <Truck className="w-4 h-4 text-gold mt-0.5 shrink-0" strokeWidth={1.5} />
                <p className="text-[0.8rem] text-gold/95 leading-relaxed">{L.product.freeShip}</p>
              </div>

              {related.length > 0 && (
                <div className="border-t border-gold/15 pt-4" data-testid="quick-view-related">
                  <p className="text-[0.6rem] tracking-[0.25em] uppercase text-sand mb-3">{L.product.related}</p>
                  <div className="flex gap-3">
                    {related.map((r) => (
                      <button
                        key={r.slug}
                        data-testid={"quick-view-related-" + r.slug}
                        onClick={() => setQuickView(r)}
                        className="group text-left"
                      >
                        <img src={r.image} alt={prodName(r, lang)} className="w-14 h-20 object-cover rounded-lg border border-gold/20 group-hover:border-gold/70 transition-colors" />
                        <p className="text-[0.6rem] text-ivory/60 mt-1.5 w-14 leading-tight truncate">{prodName(r, lang)}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
